import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { LEAD_MAGNET_KEYS, SMS_CONSENT_TAG, getLeadMagnet, leadMagnetFormId } from "./lead-magnets";

export const leadMagnetInput = z.object({
  key: z.string().refine((k) => LEAD_MAGNET_KEYS.includes(k), "Unknown lead magnet"),
  firstName: z.string().trim().min(1, "First name is required").max(80),
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: z
    .string()
    .trim()
    .min(7, "Phone is required")
    .max(30)
    .regex(/^[+()\-.\s\d]+$/, "Enter a valid phone number"),
  smsConsent: z.literal(true, { errorMap: () => ({ message: "Please agree to receive text messages" }) }),
  path: z.string().max(500).default("/"),
  sessionId: z.string().max(100).optional(),
});

const GHL_API = "https://services.leadconnectorhq.com";

export const submitLeadMagnet = createServerFn({ method: "POST" })
  .inputValidator((d) => leadMagnetInput.parse(d))
  .handler(async ({ data }) => {
    const magnet = getLeadMagnet(data.key)!;
    const { formsCredentials } = await import("./ghl-forms.server");
    const creds = formsCredentials();
    if (!creds) throw new Error("Sign-ups are temporarily unavailable. Please try again later.");

    const h = {
      Authorization: `Bearer ${creds.token}`,
      Version: "2021-07-28",
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    const upsertRes = await fetch(`${GHL_API}/contacts/upsert`, {
      method: "POST",
      headers: h,
      body: JSON.stringify({
        locationId: creds.locationId,
        firstName: data.firstName,
        email: data.email,
        phone: data.phone,
        source: `Lead magnet: ${magnet.title}`,
      }),
    });
    if (!upsertRes.ok) {
      const t = await upsertRes.text();
      console.error(`[lead-magnet] upsert failed [${upsertRes.status}]: ${t.slice(0, 300)}`);
      throw new Error("We couldn't save your details. Please try again.");
    }
    const body: any = await upsertRes.json();
    const contactId = String(body?.contact?.id ?? body?.id ?? "");
    if (!contactId) throw new Error("We couldn't save your details. Please try again.");

    const tagRes = await fetch(`${GHL_API}/contacts/${contactId}/tags`, {
      method: "POST",
      headers: h,
      body: JSON.stringify({ tags: [...magnet.tags, SMS_CONSENT_TAG] }),
    });
    if (!tagRes.ok) {
      console.error(`[lead-magnet] tagging failed [${tagRes.status}]: ${(await tagRes.text()).slice(0, 300)}`);
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("form_submissions").insert({
      step: "blog",
      ghl_id: `${contactId}:${magnet.key}`,
      form_id: leadMagnetFormId(magnet.key),
      form_name: `Lead magnet: ${magnet.title}`,
      contact_name: data.firstName,
      email: data.email,
      phone: data.phone,
      source: "lead_magnet",
      submitted_at: new Date().toISOString(),
    });
    if (error && !/duplicate|unique/i.test(error.message)) {
      console.error(`[lead-magnet] store failed: ${error.message}`);
      throw new Error("We couldn't save your details. Please try again.");
    }

    await supabaseAdmin.from("analytics_events").insert({
      type: "form_submit",
      path: data.path,
      cta: "blog",
      session_id: data.sessionId ?? null,
    });

    return { ok: true };
  });
